import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, NavLink, Routes, Route } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  FileText,
  IdCard,
  Building2,
  Settings as SettingsIcon,
  LogOut,
  Mail,
  Lock,
  Trash2,
  Save,
  Plus,
  Edit3,
  Download,
} from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { clearToken } from "../lib/auth";
import { toast } from "sonner";
import { COMPANY } from "../lib/constants";
import Logo from "../components/Logo";

function StatCard({ label, value, accent }) {
  return (
    <div className="card-premium p-5">
      <div className="text-xs uppercase tracking-widest text-[var(--melopa-muted)]">{label}</div>
      <div className={`mt-1 text-3xl font-bold ${accent || "text-navy"}`}>{value ?? "—"}</div>
    </div>
  );
}

function Sidebar({ onLogout }) {
  const links = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/leads", label: "Leads", icon: Inbox },
    { to: "/admin/documents", label: "Documents", icon: FileText },
    { to: "/admin/doc-requests", label: "Doc Requests", icon: Lock },
    { to: "/admin/contact", label: "Contact Messages", icon: Mail },
    { to: "/admin/business-card", label: "Business Card", icon: IdCard },
    { to: "/admin/company", label: "Company Profile", icon: Building2 },
    { to: "/admin/settings", label: "Site Settings", icon: SettingsIcon },
  ];
  return (
    <aside className="bg-navy text-white w-full lg:w-64 lg:min-h-screen lg:sticky lg:top-0">
      <div className="p-5 flex items-center gap-2.5 border-b border-white/10">
        <Logo size={36} tone="pill" />
        <div>
          <div className="font-bold leading-tight">Melopa Admin</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--melopa-gold-soft)]">Control Panel</div>
        </div>
      </div>
      <nav className="p-3 grid grid-cols-2 lg:grid-cols-1 gap-1">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
              }`
            }
            data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <l.icon className="w-4 h-4" />
            <span>{l.label}</span>
          </NavLink>
        ))}
        <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/70 hover:bg-white/5 hover:text-white col-span-2 lg:col-span-1" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> Logout
        </button>
        <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/60 hover:bg-white/5 hover:text-white col-span-2 lg:col-span-1">
          ← Back to website
        </Link>
      </nav>
    </aside>
  );
}

function SecurityBanner() {
  const [status, setStatus] = useState(null);
  const [showChange, setShowChange] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const reload = () => api.get("/admin/security-status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    reload();
  }, []);

  if (!status) return null;
  const warnings = [];
  if (status.using_default_password) warnings.push("default-password");
  if (status.using_default_jwt_secret) warnings.push("default-jwt");
  if (warnings.length === 0) return null;

  const submitChange = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (pwdForm.new_password.length < 10) {
      toast.error("New password must be at least 10 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success("Password updated. Please sign in again.");
      setShowChange(false);
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-[var(--melopa-saffron)]/40 bg-[#fff7ed] p-4 mb-4" data-testid="security-banner">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--melopa-saffron)]/15 grid place-items-center text-[var(--melopa-saffron)] shrink-0">!</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[var(--melopa-saffron)]">Security action required</div>
          <ul className="text-sm text-[#7c2d12] mt-1 space-y-1">
            {status.using_default_password && (
              <li>• Admin account is still using the <b>default password</b>. Please change it now.</li>
            )}
            {status.using_default_jwt_secret && (
              <li>• <b>JWT_SECRET</b> is unset or default in <code>backend/.env</code>. Generate a strong one before deploying.</li>
            )}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            {status.using_default_password && (
              <button onClick={() => setShowChange(true)} className="btn-primary !py-2 !px-3 text-sm" data-testid="open-pwd-change">
                Change password
              </button>
            )}
          </div>
        </div>
      </div>

      {showChange && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowChange(false)}>
          <form onSubmit={submitChange} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 grid gap-3" data-testid="pwd-change-form">
            <div className="kicker">Security</div>
            <h3 className="text-xl font-bold text-navy">Change admin password</h3>
            <div className="divider-gold" />
            <div>
              <label className="label">Current password</label>
              <input className="input" type="password" required value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} data-testid="pwd-current" />
            </div>
            <div>
              <label className="label">New password (min 10 chars)</label>
              <input className="input" type="password" required minLength={10} value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} data-testid="pwd-new" />
            </div>
            <div>
              <label className="label">Confirm new password</label>
              <input className="input" type="password" required value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} data-testid="pwd-confirm" />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-outline" onClick={() => setShowChange(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="pwd-submit">
                {busy ? "Saving…" : "Update password"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <div>
      <h2 className="section-title text-2xl">Dashboard Overview</h2>
      <div className="divider-gold" />
      <SecurityBanner />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Total Leads" value={stats?.total_leads} />
        <StatCard label="New Leads" value={stats?.new_leads} accent="text-[var(--melopa-saffron)]" />
        <StatCard label="Documents" value={stats?.total_documents} />
        <StatCard label="Document Downloads" value={stats?.total_downloads} />
        <StatCard label="Contact Messages" value={stats?.total_contacts} />
        <StatCard label="Pending Doc Requests" value={stats?.pending_doc_requests} accent="text-[var(--melopa-green)]" />
      </div>
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="card-premium p-5">
          <div className="kicker mb-1">Quick Links</div>
          <h3 className="font-bold text-navy">Manage</h3>
          <div className="divider-gold" />
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/leads" className="btn-outline">Leads</Link>
            <Link to="/admin/documents" className="btn-outline">Documents</Link>
            <Link to="/admin/business-card" className="btn-outline">Business Card</Link>
            <Link to="/admin/company" className="btn-outline">Company Profile</Link>
          </div>
        </div>
        <div className="card-premium p-5">
          <div className="kicker mb-1">Tip</div>
          <h3 className="font-bold text-navy">Document visibility</h3>
          <div className="divider-gold" />
          <p className="text-sm text-[var(--melopa-muted)]">
            Use <b>public</b> for general brochures and <b>protected</b> for vendor docs that require an access request.
          </p>
        </div>
      </div>
    </div>
  );
}

const LEAD_STATUSES = [
  "New",
  "Contacted",
  "Quotation Sent",
  "Under Review",
  "Converted",
  "Lost",
  "Closed",
];

function Leads() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get("/admin/leads", { params });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const updateLead = async (id, patch) => {
    try {
      const res = await api.put(`/admin/leads/${id}`, patch);
      setItems((arr) => arr.map((it) => (it.id === id ? res.data : it)));
      if (selected?.id === id) setSelected(res.data);
      toast.success("Lead updated");
    } catch {
      toast.error("Failed");
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    setItems((arr) => arr.filter((it) => it.id !== id));
    setSelected(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="section-title text-2xl">Leads</h2>
          <p className="text-sm text-[var(--melopa-muted)]">Requirement & tender enquiries.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input className="input" placeholder="Search name/phone/email" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn-primary" onClick={load}>Filter</button>
        </div>
      </div>
      <div className="divider-gold" />
      {loading ? (
        <div className="text-[var(--melopa-muted)]">Loading…</div>
      ) : (
        <div className="card-premium overflow-x-auto">
          <table className="w-full table-clean min-w-[720px]">
            <thead>
              <tr><th>Name</th><th>Org</th><th>Phone</th><th>Category</th><th>Status</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-[var(--melopa-muted)] py-6">No leads yet.</td></tr>
              ) : items.map((l) => (
                <tr key={l.id}>
                  <td><div className="font-semibold text-navy">{l.name}</div><div className="text-xs text-[var(--melopa-muted)]">{l.email || ""}</div></td>
                  <td>{l.organization || "—"}</td>
                  <td>{l.phone}</td>
                  <td>{l.requirement_category}</td>
                  <td>
                    <select className="input py-1 text-xs" value={l.status} onChange={(e) => updateLead(l.id, { status: e.target.value })}>
                      {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="text-xs">{new Date(l.created_at).toLocaleString()}</td>
                  <td>
                    <button className="btn-outline text-xs" onClick={() => setSelected(l)} data-testid={`view-lead-${l.id}`}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="kicker mb-1">Lead</div>
            <h3 className="text-xl font-bold text-navy">{selected.name}</h3>
            <div className="divider-gold" />
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><dt className="text-xs text-[var(--melopa-muted)]">Organization</dt><dd>{selected.organization || "—"}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Designation</dt><dd>{selected.designation || "—"}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Phone</dt><dd>{selected.phone}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">WhatsApp</dt><dd>{selected.whatsapp || "—"}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Email</dt><dd>{selected.email || "—"}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Category</dt><dd>{selected.requirement_category}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Quantity</dt><dd>{selected.quantity || "—"}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Location</dt><dd>{selected.delivery_location || "—"}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Deadline</dt><dd>{selected.expected_deadline || "—"}</dd></div>
              <div><dt className="text-xs text-[var(--melopa-muted)]">Budget</dt><dd>{selected.budget_range || "—"}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs text-[var(--melopa-muted)]">Details</dt><dd className="whitespace-pre-wrap">{selected.requirement_details}</dd></div>
              {selected.message && (
                <div className="sm:col-span-2"><dt className="text-xs text-[var(--melopa-muted)]">Message</dt><dd className="whitespace-pre-wrap">{selected.message}</dd></div>
              )}
              {selected.uploaded_file_url && (
                <div className="sm:col-span-2">
                  <dt className="text-xs text-[var(--melopa-muted)]">Attachment</dt>
                  <dd><a className="text-navy underline" href={fileUrl(selected.uploaded_file_url)} target="_blank" rel="noreferrer">View / Download</a></dd>
                </div>
              )}
            </dl>
            <div className="mt-4">
              <label className="label">Admin Notes</label>
              <textarea
                className="input"
                rows={3}
                defaultValue={selected.admin_notes || ""}
                onBlur={(e) => updateLead(selected.id, { admin_notes: e.target.value })}
              />
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button className="btn-outline text-red-600 border-red-200" onClick={() => removeLead(selected.id)}>
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button className="btn-primary" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Documents() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const empty = { title: "", category: "Brochure", description: "", file_url: "", visibility: "public", is_active: true };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);

  const load = async () => {
    const res = await api.get("/admin/documents");
    setItems(res.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) return form.file_url;
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const file_url = await upload();
      if (!file_url) {
        toast.error("Please upload a file");
        setBusy(false);
        return;
      }
      const payload = { ...form, file_url };
      if (editing) {
        await api.put(`/admin/documents/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/documents", payload);
        toast.success("Created");
      }
      setEditing(null);
      setForm(empty);
      setFile(null);
      load();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (d) => {
    setEditing(d);
    setForm({
      title: d.title,
      category: d.category,
      description: d.description || "",
      file_url: d.file_url,
      visibility: d.visibility,
      is_active: d.is_active,
    });
    setFile(null);
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete '${d.title}'?`)) return;
    await api.delete(`/admin/documents/${d.id}`);
    load();
  };

  return (
    <div>
      <h2 className="section-title text-2xl">Documents</h2>
      <p className="text-sm text-[var(--melopa-muted)]">Upload company profile, brochures and vendor documents.</p>
      <div className="divider-gold" />

      <form onSubmit={save} className="card-premium p-5 grid sm:grid-cols-2 gap-3 mb-6" data-testid="doc-form">
        <div>
          <label className="label">Title *</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label">Category *</label>
          <input className="input" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Brochure, Profile, Certificate…" />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Description</label>
          <textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <label className="label">Visibility</label>
          <select className="input" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
            <option value="public">Public (downloadable)</option>
            <option value="protected">Protected (request access)</option>
            <option value="admin">Admin only</option>
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })}>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">File {editing ? "(optional, replaces existing)" : "*"}</label>
          <input type="file" className="input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {form.file_url && !file && (
            <div className="text-xs mt-1">
              Current: <a className="text-navy underline" href={fileUrl(form.file_url)} target="_blank" rel="noreferrer">view</a>
            </div>
          )}
        </div>
        <div className="sm:col-span-2 flex gap-2">
          <button disabled={busy} className="btn-primary" data-testid="doc-save">
            <Save className="w-4 h-4" /> {editing ? "Update" : "Create"}
          </button>
          {editing && (
            <button type="button" className="btn-outline" onClick={() => { setEditing(null); setForm(empty); setFile(null); }}>Cancel</button>
          )}
        </div>
      </form>

      <div className="card-premium overflow-x-auto">
        <table className="w-full table-clean min-w-[720px]">
          <thead><tr><th>Title</th><th>Category</th><th>Visibility</th><th>Downloads</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-[var(--melopa-muted)] py-6">No documents yet.</td></tr>
            ) : items.map((d) => (
              <tr key={d.id}>
                <td><div className="font-semibold text-navy">{d.title}</div>{d.description && <div className="text-xs text-[var(--melopa-muted)]">{d.description}</div>}</td>
                <td>{d.category}</td>
                <td><span className="badge-soft">{d.visibility}</span></td>
                <td>{d.download_count || 0}</td>
                <td>{d.is_active ? "Yes" : "No"}</td>
                <td>
                  <div className="flex gap-2">
                    <a className="btn-outline text-xs" href={fileUrl(d.file_url)} target="_blank" rel="noreferrer"><Download className="w-3 h-3" /> Open</a>
                    <button className="btn-outline text-xs" onClick={() => startEdit(d)}><Edit3 className="w-3 h-3" /> Edit</button>
                    <button className="btn-outline text-xs text-red-600 border-red-200" onClick={() => remove(d)}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DocRequests() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/document-requests");
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/admin/document-requests/${id}`);
    load();
  };
  return (
    <div>
      <h2 className="section-title text-2xl">Document Access Requests</h2>
      <div className="divider-gold" />
      <div className="card-premium overflow-x-auto">
        <table className="w-full table-clean min-w-[720px]">
          <thead><tr><th>Name</th><th>Org</th><th>Phone</th><th>Email</th><th>Document</th><th>Purpose</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-[var(--melopa-muted)] py-6">No requests yet.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id}>
                <td className="font-semibold text-navy">{r.name}</td>
                <td>{r.organization || "—"}</td>
                <td>{r.phone}</td>
                <td>{r.email || "—"}</td>
                <td>{r.document_title || "—"}</td>
                <td className="max-w-xs">{r.purpose || "—"}</td>
                <td className="text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td><button className="btn-outline text-xs text-red-600 border-red-200" onClick={() => remove(r.id)}><Trash2 className="w-3 h-3" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContactMsgs() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/contact");
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/contact/${id}`, { status });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/contact/${id}`);
    load();
  };
  return (
    <div>
      <h2 className="section-title text-2xl">Contact Messages</h2>
      <div className="divider-gold" />
      <div className="grid gap-3">
        {items.length === 0 ? (
          <div className="card-premium p-5 text-[var(--melopa-muted)]">No messages.</div>
        ) : items.map((m) => (
          <div key={m.id} className="card-premium p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-navy">{m.name} <span className="text-xs text-[var(--melopa-muted)]">{m.email && `• ${m.email}`} {m.phone && `• ${m.phone}`}</span></div>
                {m.subject && <div className="text-sm text-[var(--melopa-muted)]">Subject: {m.subject}</div>}
              </div>
              <div className="flex gap-2">
                <select className="input py-1 text-xs" value={m.status} onChange={(e) => setStatus(m.id, e.target.value)}>
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="closed">Closed</option>
                </select>
                <button className="btn-outline text-xs text-red-600 border-red-200" onClick={() => remove(m.id)}><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
            <p className="text-sm mt-2 whitespace-pre-wrap">{m.message}</p>
            <div className="text-[11px] text-[var(--melopa-muted)] mt-2">{new Date(m.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BusinessCardEditor() {
  const [bc, setBc] = useState(null);
  useEffect(() => {
    api.get("/business-card").then((r) => setBc(r.data));
  }, []);
  if (!bc) return <div>Loading…</div>;
  const update = (k, v) => setBc((b) => ({ ...b, [k]: v }));
  const save = async () => {
    await api.put("/admin/business-card", bc);
    toast.success("Saved");
  };
  return (
    <div>
      <h2 className="section-title text-2xl">Digital Business Card</h2>
      <div className="divider-gold" />
      <div className="card-premium p-5 grid sm:grid-cols-2 gap-3">
        {[
          ["person_name", "Person Name"],
          ["designation", "Designation"],
          ["phone", "Phone"],
          ["whatsapp", "WhatsApp"],
          ["email", "Email"],
          ["website", "Website"],
          ["address", "Address"],
          ["map_link", "Google Maps Link"],
          ["qr_url", "QR Code URL"],
          ["profile_pdf", "Profile PDF URL"],
        ].map(([k, lab]) => (
          <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
            <label className="label">{lab}</label>
            <input className="input" value={bc[k] || ""} onChange={(e) => update(k, e.target.value)} data-testid={`bc-${k}`} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <button className="btn-primary" onClick={save} data-testid="bc-save"><Save className="w-4 h-4" /> Save</button>
        </div>
      </div>
    </div>
  );
}

function CompanyProfileEditor() {
  const [cp, setCp] = useState(null);
  useEffect(() => {
    api.get("/company-profile").then((r) => setCp(r.data));
  }, []);
  if (!cp) return <div>Loading…</div>;
  const update = (k, v) => setCp((b) => ({ ...b, [k]: v }));
  const save = async () => {
    await api.put("/admin/company-profile", cp);
    toast.success("Saved");
  };
  return (
    <div>
      <h2 className="section-title text-2xl">Company Profile</h2>
      <div className="divider-gold" />
      <div className="card-premium p-5 grid sm:grid-cols-2 gap-3">
        {[
          ["company_name", "Company Name"],
          ["cin", "CIN"],
          ["gstin", "GSTIN"],
          ["msme", "MSME / Udyam"],
          ["fssai", "FSSAI"],
          ["phone", "Phone"],
          ["whatsapp", "WhatsApp"],
          ["email", "Email"],
          ["website", "Website"],
          ["address", "Address"],
        ].map(([k, lab]) => (
          <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
            <label className="label">{lab}</label>
            <input className="input" value={cp[k] || ""} onChange={(e) => update(k, e.target.value)} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label className="label">About Text</label>
          <textarea className="input" rows={5} value={cp.about_text || ""} onChange={(e) => update("about_text", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <button className="btn-primary" onClick={save}><Save className="w-4 h-4" /> Save</button>
        </div>
      </div>
    </div>
  );
}

function pdfButtonLabel(uploading, currentUrl) {
  if (uploading) return "Uploading…";
  if (currentUrl) return "Replace PDF";
  return "Upload PDF";
}

function SiteSettings() {
  const [s, setS] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    api.get("/site-settings").then((r) => setS(r.data));
  }, []);
  if (!s) return <div>Loading…</div>;
  const update = (k, v) => setS((b) => ({ ...b, [k]: v }));
  const save = async () => {
    await api.put("/admin/site-settings", s);
    toast.success("Saved");
  };
  const onPdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...s, company_profile_pdf_url: r.data.url };
      setS(next);
      await api.put("/admin/site-settings", next);
      toast.success("Company profile PDF uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <h2 className="section-title text-2xl">Site Settings</h2>
      <div className="divider-gold" />

      {/* Company Profile PDF uploader */}
      <div className="card-premium p-5 mb-6">
        <div className="kicker mb-1">Company Profile PDF</div>
        <h3 className="text-lg font-bold text-navy">Upload the master profile</h3>
        <div className="divider-gold" />
        <p className="text-sm text-[var(--melopa-muted)]">
          This PDF is used by every "Download Company Profile" button across the website (home, digital card, downloads, CTAs).
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="btn-primary cursor-pointer" data-testid="pdf-upload-btn">
            <Download className="w-4 h-4 rotate-180" />
            {pdfButtonLabel(uploading, s.company_profile_pdf_url)}
            <input type="file" accept="application/pdf" className="hidden" onChange={onPdfUpload} disabled={uploading} />
          </label>
          {s.company_profile_pdf_url && (
            <>
              <a href={fileUrl(s.company_profile_pdf_url)} target="_blank" rel="noreferrer" className="btn-outline" data-testid="pdf-current-link">
                View current PDF
              </a>
              <button
                className="btn-outline text-red-600 border-red-200"
                onClick={async () => {
                  if (!window.confirm("Remove the current PDF? Download buttons will fall back to the Downloads page.")) return;
                  const next = { ...s, company_profile_pdf_url: null };
                  setS(next);
                  await api.put("/admin/site-settings", next);
                  toast.success("Removed");
                }}
              >
                <Trash2 className="w-4 h-4" /> Remove
              </button>
            </>
          )}
        </div>
        {s.company_profile_pdf_url && (
          <div className="text-xs text-[var(--melopa-muted)] mt-2 break-all">{s.company_profile_pdf_url}</div>
        )}
      </div>

      <div className="card-premium p-5 grid sm:grid-cols-2 gap-3">
        {[
          ["whatsapp_number", "WhatsApp Number (with country code, no +)"],
          ["phone_display", "Phone Display"],
          ["email", "Email"],
          ["address", "Address"],
        ].map(([k, lab]) => (
          <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
            <label className="label">{lab}</label>
            <input className="input" value={s[k] || ""} onChange={(e) => update(k, e.target.value)} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <button className="btn-primary" onClick={save}><Save className="w-4 h-4" /> Save</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const onLogout = () => {
    clearToken();
    nav("/admin/login");
  };
  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-soft">
      <Sidebar onLogout={onLogout} />
      <div className="flex-1 p-5 md:p-8">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="leads" element={<Leads />} />
          <Route path="documents" element={<Documents />} />
          <Route path="doc-requests" element={<DocRequests />} />
          <Route path="contact" element={<ContactMsgs />} />
          <Route path="business-card" element={<BusinessCardEditor />} />
          <Route path="company" element={<CompanyProfileEditor />} />
          <Route path="settings" element={<SiteSettings />} />
        </Routes>
      </div>
    </div>
  );
}
